import React, { ReactNode, useRef, useState } from "react"

import { gql, useClient } from "urql"
import { pipe, subscribe } from "wonka"

import { AlertLevel, Color } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import {
  AlertMessageBox,
  Button,
  DescriptionItem,
  StyledLink,
} from "~tailwindui/Basics"
import Panel from "~tailwindui/Basics/Panel"

import commaList from "src/commaList"

import ConsistentTutorWarning from "components/ConsistentTutorWarning"
import LocalTime from "components/LocalTime"

type Availability = {
  id: string
  startsAt: Date
  endsAt: Date
  weekday: string
  duration: string
}

export type SectionCardProps = {
  courseSection: {
    id: string
    startsOn: Date
    endsOn: Date
    maxSubsEstimate: number
    organization: {
      minTutorConsistencyRate: number
      name: string
      details: string
      emailTimeZone: string
    }
    availabilities: Availability[]
  }
  courseCluster: {
    id: string
    name: string
    description: string
    grades: {
      id: string
      name: string
    }[]
    subject: {
      id: string
      name: string
    }
  }
}

const SectionCard: React.FC<SectionCardProps> = ({
  courseCluster,
  courseSection,
}) => {
  const [statusMessage, setStatusMessage] = useState<ReactNode | null>()
  const [success, setSuccess] = useState<ReactNode | null>()
  const [errorMessages, setErrorMessages] = useState([])
  const [inFlight, setInFlight] = useState(false)
  const submitClicked = useRef(false)
  const client = useClient()

  const register = () => {
    if (submitClicked.current) {
      return
    }
    submitClicked.current = true
    setInFlight(true)
    setErrorMessages([])

    const { unsubscribe } = pipe(
      client.subscription(registerSubscription, {
        courseSectionId: courseSection.id,
      }),
      subscribe(result => {
        if (result.data) {
          const { status, errorMessages, studyGroup } =
            result.data.courseSectionRegister
          setErrorMessages(errorMessages)
          if (errorMessages.length) {
            setErrorMessages(errorMessages)
            setInFlight(false)
            submitClicked.current = false
            unsubscribe()
          }
          if (status === "in-progress") {
            setStatusMessage(
              <>
                In Progress <br />
                (Please be patient, this process can take some time)
              </>
            )
          }
          if (status === "success") {
            setErrorMessages([])
            setStatusMessage(null)
            setSuccess(
              <>
                Registration Successful!
                <br />
                <StyledLink
                  to={path("studyGroupDetails", { id: studyGroup.id })}
                >
                  Click here to view your new group
                </StyledLink>
              </>
            )
            unsubscribe()
          }
        } else if (result.error) {
          setErrorMessages([result.error?.message])
          setInFlight(false)
          submitClicked.current = false
          unsubscribe()
        }
      })
    )
  }

  return (
    <Panel className="flex max-w-lg max-w-xl flex-col">
      <div>
        {(courseSection.maxSubsEstimate ||
          courseSection.maxSubsEstimate === 0) && (
          <ConsistentTutorWarning
            minTutorConsistencyRate={
              courseSection.organization.minTutorConsistencyRate
            }
            maxSubsEstimate={courseSection.maxSubsEstimate}
          />
        )}
      </div>

      <div className="flex flex-1 flex-col items-center justify-between space-y-3 py-4 px-6">
        <div className="text-center text-lg font-bold text-green-600">
          <span>{courseCluster.name}</span>
        </div>

        <div className="text-sm">
          <DescriptionItem
            label="Meeting Times"
            value={
              <ul>
                {courseSection.availabilities.map(availability => (
                  <div key={availability.id}>
                    {availability.weekday}{" "}
                    <LocalTime timestamp={availability.startsAt} omitDate /> to{" "}
                    <LocalTime
                      timestamp={availability.endsAt}
                      format="h:mm A (z)"
                    />
                  </div>
                ))}
              </ul>
            }
          />
          <DescriptionItem
            label="Course Dates"
            value={
              <>
                <LocalTime omitTime timestamp={courseSection.startsOn} /> to{" "}
                <LocalTime omitTime timestamp={courseSection.endsOn} />
              </>
            }
          />
          <hr className="my-2" />

          <DescriptionItem label="Subject" value={courseCluster.subject.name} />
          <DescriptionItem
            label="Grades"
            value={commaList(courseCluster.grades.map(grade => grade.name))}
          />
          <DescriptionItem
            label="Course Description"
            value={courseCluster.description}
          />

          {courseSection.organization && (
            <>
              <DescriptionItem
                label="Organization"
                value={courseSection.organization.name}
              />
              {courseSection.organization.details && (
                <DescriptionItem
                  label="Organization Details"
                  value={courseSection.organization.details}
                />
              )}
              <DescriptionItem
                label="Organization Time Zone"
                value={courseSection.organization.emailTimeZone}
              />
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col justify-center">
        {statusMessage ? (
          <AlertMessageBox level={AlertLevel.Info}>
            Current Status: {statusMessage}
          </AlertMessageBox>
        ) : success ? (
          <AlertMessageBox level={AlertLevel.Success}>
            {success}
          </AlertMessageBox>
        ) : null}

        {errorMessages.length > 0 && (
          <AlertMessageBox>
            <div className="flex list-disc flex-col space-y-1 pl-5">
              {errorMessages.map((msg, index) => (
                <div key={index} dangerouslySetInnerHTML={{ __html: msg }} />
              ))}
            </div>
          </AlertMessageBox>
        )}
        <div className="flex w-full items-center justify-center gap-4">
          <Button color={Color.Teal} onClick={register} disabled={inFlight}>
            Register
          </Button>
        </div>
      </div>
    </Panel>
  )
}

const registerSubscription = gql`
  subscription ($courseSectionId: ID) {
    courseSectionRegister(courseSectionId: $courseSectionId) {
      status
      errorMessages
      studyGroup {
        id
      }
    }
  }
`

export default SectionCard
